import React from 'react';
import {
  Paper,
  Title,
  Stack,
  Progress,
  Text,
  Group,
  Badge,
  Alert,
  Loader
} from '@mantine/core';
import {
  IconCheck,
  IconAlertCircle,
  IconClock,
  IconPlayerPlay
} from '@tabler/icons-react';

import { RESOURCE_TYPE_LABELS, RESOURCE_STATE } from '../import/importConstants';

/**
 * ImportProgressPanel - Real-time progress display during import
 */
const ImportProgressPanel = ({ importProgress, importStatus }) => {
  if (!importProgress || Object.keys(importProgress).length === 0) {
    return null;
  }

  /**
   * Get progress percentage for a resource type
   */
  const getProgressPercentage = (resourceTypeProgress) => {
    if (!resourceTypeProgress || resourceTypeProgress.total === 0) {
      return 0;
    }

    return Math.round((resourceTypeProgress.current / resourceTypeProgress.total) * 100);
  };

  /**
   * Get status badge for a resource type
   */
  const getStatusBadge = (state) => {
    switch (state) {
      case RESOURCE_STATE.PENDING:
        return (
          <Badge color="gray" leftSection={<IconClock size={14} />}>
            Pending
          </Badge>
        );
      case RESOURCE_STATE.PROCESSING:
        return (
          <Badge color="blue" leftSection={<Loader size={14} />}>
            Processing
          </Badge>
        );
      case RESOURCE_STATE.SUCCESS:
        return (
          <Badge color="green" leftSection={<IconCheck size={14} />}>
            Complete
          </Badge>
        );
      case RESOURCE_STATE.FAILED:
        return (
          <Badge color="red" leftSection={<IconAlertCircle size={14} />}>
            Failed
          </Badge>
        );
      default:
        return null;
    }
  };

  /**
   * Get progress color
   */
  const getProgressColor = (state) => {
    switch (state) {
      case RESOURCE_STATE.SUCCESS:
        return 'green';
      case RESOURCE_STATE.FAILED:
        return 'red';
      case RESOURCE_STATE.PROCESSING:
        return 'blue';
      default:
        return 'gray';
    }
  };

  /**
   * Calculate overall progress
   */
  const calculateOverallProgress = () => {
    const resourceTypes = Object.keys(importProgress);
    if (resourceTypes.length === 0) return 0;

    let totalCurrent = 0;
    let totalMax = 0;

    resourceTypes.forEach(resourceType => {
      const progress = importProgress[resourceType];
      totalCurrent += progress.current || 0;
      totalMax += progress.total || 0;
    });

    return totalMax > 0 ? Math.round((totalCurrent / totalMax) * 100) : 0;
  };

  const overallProgress = calculateOverallProgress();
  const isImporting = importStatus === 'importing';

  return (
    <Paper shadow="sm" p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Group>
            <IconPlayerPlay size={24} />
            <Title order={3}>Import Progress</Title>
          </Group>
          {isImporting && (
            <Badge size="lg" color="blue" leftSection={<Loader size={14} />}>
              Import in Progress
            </Badge>
          )}
        </Group>

        {/* Overall Progress */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={500}>Overall Progress</Text>
            <Text size="sm" c="dimmed">{overallProgress}%</Text>
          </Group>
          <Progress
            value={overallProgress}
            size="xl"
            radius="md"
            striped={isImporting}
            animated={isImporting}
            color="aws"
          />
        </div>

        {/* Resource Type Progress */}
        <Stack gap="sm">
          {Object.keys(importProgress).map(resourceType => {
            const progress = importProgress[resourceType];
            const percentage = getProgressPercentage(progress);

            return (
              <div key={resourceType}>
                <Group justify="space-between" mb="xs">
                  <Group gap="xs">
                    <Text size="sm" fw={500}>
                      {RESOURCE_TYPE_LABELS[resourceType] || resourceType}
                    </Text>
                    {getStatusBadge(progress.state)}
                  </Group>
                  <Text size="sm" c="dimmed">
                    {progress.current} / {progress.total}
                  </Text>
                </Group>

                <Progress
                  value={percentage}
                  size="md"
                  radius="sm"
                  color={getProgressColor(progress.state)}
                  striped={progress.state === RESOURCE_STATE.PROCESSING}
                  animated={progress.state === RESOURCE_STATE.PROCESSING}
                />

                {progress.message && (
                  <Text size="xs" c="dimmed" mt={4}>
                    {progress.message}
                  </Text>
                )}

                {progress.state === RESOURCE_STATE.FAILED && progress.error && (
                  <Alert
                    icon={<IconAlertCircle size={14} />}
                    color="red"
                    variant="light"
                    mt="xs"
                  >
                    <Text size="xs">{progress.error}</Text>
                  </Alert>
                )}
              </div>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default ImportProgressPanel;
